import React, { useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, LabelList } from 'recharts';
import RegistrosService, { Registro, AudioLink } from '../../services/registrosService';

// Lookup genérico (id -> nome). Emissora traz cidade/estado opcionais.
interface Opcao { id: number; nome: string; cidade?: string; estado?: string; }

interface Props {
  registros: Registro[];
  emissoras: Opcao[];
  marcas: Opcao[];
  itens: Opcao[];
  anunciantes: Opcao[];
  programas: Opcao[];
  mostrarCustos: boolean;
  agruparPorCampanha?: boolean; // false (padrão) = lista corrida; true = agrupado por campanha
  observacao?: string;          // texto livre que aparece no fim do relatório
  incluirGraficos?: boolean;    // inclui gráficos (volume diário + distribuição por horário) no PDF
  audios?: Record<string, AudioLink>; // registro_id -> link do áudio da rádio
}

// ---- helpers de formatação (locais, para não depender do pai) ----
const fmtData = (dh?: string) => {
  if (!dh) return '';
  const d = dh.substring(0, 10).split('-'); // YYYY-MM-DD
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : dh;
};
const fmtHora = (dh?: string) => (dh && dh.length >= 16 ? (dh.length >= 19 ? dh.substring(11, 19) : dh.substring(11, 16)) : '');
const fmtDur = (s?: number) => `${s || 0}s`;
const fmtDurTotal = (s?: number) => {
  const seg = s || 0;
  const m = Math.floor(seg / 60);
  const r = seg % 60;
  return m > 0 ? `${m}min ${r}s` : `${r}s`;
};
const fmtBRL = (v?: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// waveform (elemento de assinatura) — barras variando de altura
const WAVE_H = [4,8,14,6,18,10,4,16,8,22,12,6,16,4,10,20,8,14,4,18,10,6,16,22,8,4,14,10,20,6,16,4,12,22,10,6,18,8,14,4,20,10,6,16,22,8,4,14,10,20,6,16,4,10,22,6,10,18,8,14,4,20,10,6,16,22,8,4,14,10,20,6,16,4,12,22,10,6,18,8,14,4,20,10,6,16,22,8,4,10,6,18,8,14,4,20,12,6,8,14,8,4,10,6];
const Waveform: React.FC = () => (
  <svg className="ccp-signal" viewBox="0 0 960 22" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="10" width="960" height="2" fill="#DCE4E7" />
    <g fill="#1C8FAE">
      {WAVE_H.map((h, i) => (
        <rect key={i} x={24 + i * 8} y={Math.floor((22 - h) / 2)} width="3" height={h} />
      ))}
    </g>
  </svg>
);

const CSS = `
.ccp-checking{font-family:'IBM Plex Sans','Segoe UI',sans-serif;color:#16232B;}
.ccp-checking .header{background:#FFF;border:1px solid #DCE4E7;border-radius:10px;overflow:hidden;}
.ccp-checking .top-row{display:flex;align-items:center;gap:28px;padding:26px 32px 22px;}
.ccp-checking .logo-wrap{flex:0 0 auto;display:flex;align-items:center;height:44px;}
.ccp-checking .logo-wrap img{height:100%;width:auto;display:block;}
.ccp-checking .divider-v{flex:0 0 auto;width:1px;align-self:stretch;background:#DCE4E7;}
.ccp-checking .title-block{flex:1 1 auto;min-width:0;}
.ccp-checking .eyebrow{font-weight:600;font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#0E5870;margin:0 0 4px;}
.ccp-checking .title{font-weight:700;font-size:22px;color:#16232B;margin:0;line-height:1.2;}
.ccp-checking .subtitle{font-size:13px;color:#6C7C84;margin:4px 0 0;}
.ccp-checking .ccp-signal{display:block;width:100%;height:22px;background:#FFF;margin-bottom:16px;}
.ccp-checking .data-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));background:#FFF;margin-bottom:6px;}
.ccp-checking .field{padding:16px 24px;border-top:1px solid #DCE4E7;}
.ccp-checking .field + .field{border-left:1px solid #DCE4E7;}
.ccp-checking .field:nth-child(3n+1){border-left:none;}
.ccp-checking .field-label{font-size:10.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#6C7C84;margin:0 0 5px;}
.ccp-checking .field-value{font-size:14.5px;font-weight:500;color:#16232B;margin:0;word-break:break-word;min-height:1em;}
.ccp-checking .field-value.code{font-family:'IBM Plex Mono',monospace;font-weight:600;color:#0E5870;}
.ccp-checking .field-value.na{font-style:italic;color:#9AA7AC;font-weight:400;}
.ccp-checking .body-card{background:#FFF;border:1px solid #DCE4E7;border-radius:10px;overflow:hidden;margin-top:16px;}
.ccp-checking table{width:100%;border-collapse:collapse;font-size:12.5px;}
.ccp-checking thead th{background:#0E5870;color:#fff;font-weight:600;font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;text-align:left;padding:10px 12px;white-space:nowrap;}
.ccp-checking tbody td{padding:9px 12px;border-bottom:1px solid #DCE4E7;color:#16232B;vertical-align:top;}
.ccp-checking td.mono{font-family:'IBM Plex Mono',monospace;color:#146D89;white-space:nowrap;}
.ccp-checking td.num{text-align:right;font-family:'IBM Plex Mono',monospace;white-space:nowrap;}
.ccp-checking td.emis{font-weight:600;}
.ccp-checking td.camp{font-weight:600;color:#0E5870;}
.ccp-checking .tipo-badge{display:inline-block;font-size:10.5px;font-weight:600;color:#0E5870;background:#EAF3F5;border:1px solid #CFE4E9;border-radius:5px;padding:2px 7px;white-space:nowrap;}
.ccp-checking tfoot td{padding:11px 12px;font-weight:700;font-size:12px;color:#0E5870;background:#EFF5F6;border-top:2px solid #1C8FAE;}
.ccp-checking .anun-row td{padding:12px 12px 11px;background:#0E5870;color:#fff;font-weight:700;font-size:12.5px;letter-spacing:.03em;}
.ccp-checking .emis-row td{padding:9px 12px;background:#DCE9EC;border-top:1px solid #C7D8DC;border-bottom:1px solid #C7D8DC;}
.ccp-checking .emis-name{font-weight:700;font-size:12.5px;color:#0E5870;}
.ccp-checking .emis-name::before{content:"";display:inline-block;width:6px;height:13px;background:#0E5870;border-radius:2px;margin-right:8px;vertical-align:-2px;}
.ccp-checking .camp-row td{padding:11px 12px 10px;background:#EAF3F5;border-top:1px solid #DCE4E7;border-bottom:1px solid #DCE4E7;}
.ccp-checking .camp-name{font-weight:700;font-size:13.5px;color:#0E5870;}
.ccp-checking .camp-name::before{content:"";display:inline-block;width:6px;height:14px;background:#1C8FAE;border-radius:2px;margin-right:9px;vertical-align:-2px;}
.ccp-checking .camp-prod{margin-left:12px;font-size:11.5px;color:#6C7C84;font-weight:500;}
.ccp-checking .camp-meta{float:right;font-family:'IBM Plex Mono',monospace;font-size:11px;color:#6C7C84;font-weight:400;}
.ccp-checking .subtotal td{padding:8px 12px;font-family:'IBM Plex Mono',monospace;font-size:11.5px;font-weight:600;color:#146D89;background:#F2F8F9;border-bottom:1px solid #DCE4E7;}
.ccp-checking td.audio{white-space:nowrap;text-align:center;}
.ccp-checking td.audio a{color:#146D89;font-weight:600;font-size:11.5px;text-decoration:none;border:1px solid #CFE4E9;background:#EAF3F5;border-radius:5px;padding:2px 8px;display:inline-block;}
.ccp-checking td.audio .audio-na{color:#9AA7AC;}
.ccp-checking .graf-card{margin-top:16px;}
.ccp-checking .graf-item{background:#FFF;border:1px solid #DCE4E7;border-radius:10px;padding:18px 24px 20px;margin-bottom:16px;break-inside:avoid;page-break-inside:avoid;}
.ccp-checking .graf-item:last-child{margin-bottom:0;}
.ccp-checking .graf-titulo{font-weight:700;font-size:13px;color:#0E5870;margin:0 0 8px;break-after:avoid;page-break-after:avoid;}
.ccp-checking .graf-wrap{display:flex;justify-content:center;}
.ccp-checking .obs-card{background:#FFF;border:1px solid #DCE4E7;border-radius:10px;margin-top:16px;padding:16px 24px;break-inside:avoid;page-break-inside:avoid;}
.ccp-checking .obs-label{font-size:10.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#6C7C84;margin:0 0 6px;}
.ccp-checking .obs-text{font-size:13px;color:#16232B;white-space:pre-wrap;line-height:1.5;margin:0;}
`;

interface CampanhaGrupo {
  campanha: string;
  marcaNome: string;
  itemNome: string;
  registros: Registro[];
  totalComerciais: number;
  totalDuracao: number;
  totalCusto: number;
}
interface AnuncianteGrupo {
  anuncianteNome: string;
  campanhas: CampanhaGrupo[];
}

const CheckingReport: React.FC<Props> = ({
  registros, emissoras, marcas, itens, anunciantes, programas,
  mostrarCustos, agruparPorCampanha = false, observacao, incluirGraficos = false,
  audios,
}) => {
  // A coluna de áudio só aparece se pelo menos um registro tiver gravação —
  // relatório de TV continua idêntico ao de antes.
  const temAudio = useMemo(
    () => !!audios && registros.some(r => audios[String(r.id)]?.disponivel),
    [audios, registros],
  );
  const urlAudio = (r: Registro) => RegistrosService.urlAbsolutaAudio(audios?.[String(r.id)]);
  const nomeDe = (lista: Opcao[], id?: number | null) =>
    id ? (lista.find(o => o.id === id)?.nome || '') : '';

  const dados = useMemo(() => {
    const anunMap: Record<string, { anuncianteNome: string; campanhas: Record<string, CampanhaGrupo> }> = {};

    registros.forEach(r => {
      const anom = nomeDe(anunciantes, r.anunciante_id) || 'Sem anunciante';
      const camp = (r.titulo && r.titulo.trim()) || 'Sem título';
      if (!anunMap[anom]) anunMap[anom] = { anuncianteNome: anom, campanhas: {} };
      const cm = anunMap[anom].campanhas;
      if (!cm[camp]) {
        cm[camp] = {
          campanha: camp,
          marcaNome: nomeDe(marcas, r.marca_id),
          itemNome: nomeDe(itens, r.item_id),
          registros: [], totalComerciais: 0, totalDuracao: 0, totalCusto: 0,
        };
      }
      const g = cm[camp];
      g.registros.push(r);
      g.totalComerciais += 1;
      g.totalDuracao += r.duracao || 0;
      g.totalCusto += r.custo || 0;
    });

    const anunLista: AnuncianteGrupo[] = Object.values(anunMap)
      .sort((a, b) => a.anuncianteNome.localeCompare(b.anuncianteNome))
      .map(a => ({
        anuncianteNome: a.anuncianteNome,
        campanhas: Object.values(a.campanhas)
          .sort((x, y) => x.campanha.localeCompare(y.campanha))
          .map(c => ({ ...c, registros: [...c.registros].sort((p, q) => (p.datahora || '').localeCompare(q.datahora || '')) })),
      }));

    const numCampanhas = anunLista.reduce((s, a) => s + a.campanhas.length, 0);
    const multiAnun = anunLista.length > 1;

    // lista corrida separada POR EMISSORA (cada uma com sub-cabeçalho)
    const emisMap: Record<string, { emissoraNome: string; registros: Registro[]; totalComerciais: number; totalDuracao: number; totalCusto: number }> = {};
    registros.forEach(r => {
      const en = nomeDe(emissoras, r.emissora_id) || 'Sem emissora';
      if (!emisMap[en]) emisMap[en] = { emissoraNome: en, registros: [], totalComerciais: 0, totalDuracao: 0, totalCusto: 0 };
      const g = emisMap[en];
      g.registros.push(r);
      g.totalComerciais += 1;
      g.totalDuracao += r.duracao || 0;
      g.totalCusto += r.custo || 0;
    });
    const emissoraGrupos = Object.values(emisMap)
      .sort((a, b) => a.emissoraNome.localeCompare(b.emissoraNome))
      .map(g => ({ ...g, registros: [...g.registros].sort((p, q) => (p.datahora || '').localeCompare(q.datahora || '')) }));

    // ---- campos do cabeçalho ----
    const anuncianteHeader = anunLista.length === 0 ? '' : anunLista.length === 1 ? anunLista[0].anuncianteNome : 'Diversos';
    const campanhaHeader = numCampanhas === 0 ? '' : numCampanhas === 1 ? anunLista[0].campanhas[0].campanha : `${numCampanhas} campanhas`;

    const emisNomes = Array.from(new Set(registros.map(r => nomeDe(emissoras, r.emissora_id)).filter(Boolean))).sort();
    const emissorasStr = emisNomes.join(', ');

    const estados = Array.from(new Set(registros.map(r => emissoras.find(e => e.id === r.emissora_id)?.estado).filter(Boolean) as string[])).sort();
    const mercados = Array.from(new Set(registros.map(r => r.mercado).filter(Boolean) as string[])).sort();
    const pracaStr = [estados.join(', '), mercados.join(' / ')].filter(Boolean).join(' · ');

    const datas = registros.map(r => r.datahora).filter(Boolean).sort() as string[];
    let periodoStr = '';
    if (datas.length) {
      const ini = fmtData(datas[0]);
      const fim = fmtData(datas[datas.length - 1]);
      periodoStr = ini === fim ? ini : `${ini} a ${fim}`;
    }

    const pis = Array.from(new Set(registros.map(r => (r.pi_number || '').trim()).filter(Boolean)));
    const piStr = pis.length === 0 ? '' : pis.length === 1 ? pis[0] : 'Diversas';

    const totalComerciais = registros.length;
    const totalDuracao = registros.reduce((s, r) => s + (r.duracao || 0), 0);
    const totalCusto = registros.reduce((s, r) => s + (r.custo || 0), 0);

    // ---- dados dos gráficos (contagem de inserções por dia e por hora) ----
    const diaMap: Record<string, { label: string; count: number }> = {};
    const horaMap: Record<number, number> = {};
    registros.forEach(r => {
      if (!r.datahora) return;
      const iso = r.datahora.substring(0, 10);
      if (!diaMap[iso]) diaMap[iso] = { label: fmtData(r.datahora).substring(0, 5), count: 0 };
      diaMap[iso].count += 1;
      const h = parseInt(r.datahora.substring(11, 13), 10);
      if (!isNaN(h)) horaMap[h] = (horaMap[h] || 0) + 1;
    });
    const dailyData = Object.keys(diaMap).sort().map(iso => ({ formattedDate: diaMap[iso].label, count: diaMap[iso].count }));
    const hourlyData: { hour: string; count: number }[] = [];
    for (let h = 0; h < 24; h++) {
      if (horaMap[h]) hourlyData.push({ hour: `${String(h).padStart(2, '0')}h`, count: horaMap[h] });
    }

    return {
      anunLista, emissoraGrupos, multiAnun,
      anuncianteHeader, campanhaHeader, emissorasStr, pracaStr, periodoStr, piStr,
      totalComerciais, totalDuracao, totalCusto,
      dailyData, hourlyData,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registros, emissoras, marcas, itens, anunciantes]);

  // Data, Hora, (Emissora|Campanha), Programa, Tipo = 5 colunas antes de Duração.
  const COLS_ANTES_DURACAO = 5;
  const NCOLS = COLS_ANTES_DURACAO + 1 + (mostrarCustos ? 1 : 0) + (temAudio ? 1 : 0);

  const linha = (r: Registro) => {
    const url = urlAudio(r);
    return (
      <tr key={r.id}>
        <td className="mono">{fmtData(r.datahora)}</td>
        <td className="mono">{fmtHora(r.datahora)}</td>
        {agruparPorCampanha
          ? <td className="emis">{nomeDe(emissoras, r.emissora_id) || '—'}</td>
          : <td className="camp">{(r.titulo && r.titulo.trim()) || '—'}</td>}
        <td>{nomeDe(programas, r.programa_id) || '—'}</td>
        <td><span className="tipo-badge">{r.tipo || '—'}</span></td>
        <td className="num">{fmtDur(r.duracao)}</td>
        {mostrarCustos && <td className="num">{fmtBRL(r.custo)}</td>}
        {temAudio && (
          <td className="audio">
            {url
              ? <a href={url} target="_blank" rel="noreferrer">▶ ouvir</a>
              : <span className="audio-na">—</span>}
          </td>
        )}
      </tr>
    );
  };

  const campSubHeader = (c: CampanhaGrupo) => {
    const prod = [c.marcaNome, c.itemNome].filter(Boolean).join(' · ');
    return (
      <tr className="camp-row" key={`camp-${c.campanha}`}>
        <td colSpan={NCOLS}>
          <span className="camp-name">{c.campanha}</span>
          {prod && <span className="camp-prod">{prod}</span>}
          <span className="camp-meta">{c.totalComerciais} inserç{c.totalComerciais === 1 ? 'ão' : 'ões'}</span>
        </td>
      </tr>
    );
  };

  const subtotalRow = (c: CampanhaGrupo) => (
    <tr className="subtotal" key={`sub-${c.campanha}`}>
      <td colSpan={COLS_ANTES_DURACAO}>Subtotal — {c.totalComerciais} inserç{c.totalComerciais === 1 ? 'ão' : 'ões'}</td>
      <td className="num">{fmtDurTotal(c.totalDuracao)}</td>
      {mostrarCustos && <td className="num">{fmtBRL(c.totalCusto)}</td>}
      {temAudio && <td />}
    </tr>
  );

  const body: React.ReactNode[] = [];
  if (agruparPorCampanha) {
    dados.anunLista.forEach(a => {
      if (dados.multiAnun) {
        body.push(
          <tr className="anun-row" key={`anun-${a.anuncianteNome}`}>
            <td colSpan={NCOLS}>{a.anuncianteNome}</td>
          </tr>
        );
      }
      a.campanhas.forEach(c => {
        body.push(campSubHeader(c));
        c.registros.forEach(r => body.push(linha(r)));
        body.push(subtotalRow(c));
      });
    });
  } else {
    // Lista corrida separada por emissora (cada emissora com sub-cabeçalho + subtotal)
    dados.emissoraGrupos.forEach(g => {
      body.push(
        <tr className="emis-row" key={`emis-${g.emissoraNome}`}>
          <td colSpan={NCOLS}>
            <span className="emis-name">{g.emissoraNome}</span>
            <span className="camp-meta">{g.totalComerciais} inserç{g.totalComerciais === 1 ? 'ão' : 'ões'}</span>
          </td>
        </tr>
      );
      g.registros.forEach(r => body.push(linha(r)));
      body.push(
        <tr className="subtotal" key={`emis-sub-${g.emissoraNome}`}>
          <td colSpan={COLS_ANTES_DURACAO}>Subtotal {g.emissoraNome} — {g.totalComerciais} inserç{g.totalComerciais === 1 ? 'ão' : 'ões'}</td>
          <td className="num">{fmtDurTotal(g.totalDuracao)}</td>
          {mostrarCustos && <td className="num">{fmtBRL(g.totalCusto)}</td>}
          {temAudio && <td />}
        </tr>
      );
    });
  }

  return (
    <div className="ccp-checking">
      <style>{CSS}</style>

      <div className="header">
        <div className="top-row">
          <div className="logo-wrap"><img src="/logo-ccp.png" alt="Controle Catarinense de Publicidade" /></div>
          <div className="divider-v" />
          <div className="title-block">
            <p className="eyebrow">Controle Catarinense de Publicidade</p>
            <h1 className="title">Relatório de Checking</h1>
            <p className="subtitle">Verificação de veiculação de campanha publicitária</p>
          </div>
        </div>

        <Waveform />

        <div className="data-grid">
          <div className="field"><p className="field-label">Anunciante</p><p className="field-value">{dados.anuncianteHeader || ' '}</p></div>
          <div className="field"><p className="field-label">Nº da PI</p>{dados.piStr
            ? <p className="field-value code">{dados.piStr}</p>
            : <p className="field-value na">não disponível</p>}</div>
          <div className="field"><p className="field-label">Campanha</p><p className="field-value">{dados.campanhaHeader || ' '}</p></div>
          <div className="field"><p className="field-label">Período</p><p className="field-value">{dados.periodoStr || ' '}</p></div>
          <div className="field"><p className="field-label">Emissoras</p><p className="field-value">{dados.emissorasStr || ' '}</p></div>
          <div className="field"><p className="field-label">Praça / Mercado</p><p className="field-value">{dados.pracaStr || ' '}</p></div>
        </div>
      </div>

      <div className="body-card">
        <table>
          <thead>
            <tr>
              <th>Data</th><th>Hora</th>
              <th>{agruparPorCampanha ? 'Emissora' : 'Campanha'}</th>
              <th>Programa</th><th>Tipo</th><th>Duração</th>
              {mostrarCustos && <th>Custo</th>}
              {temAudio && <th>Áudio</th>}
            </tr>
          </thead>
          <tbody>{body}</tbody>
          <tfoot>
            <tr>
              <td colSpan={COLS_ANTES_DURACAO}>Total — {dados.totalComerciais} inserç{dados.totalComerciais === 1 ? 'ão' : 'ões'}</td>
              <td className="num">{fmtDurTotal(dados.totalDuracao)}</td>
              {mostrarCustos && <td className="num">{fmtBRL(dados.totalCusto)}</td>}
              {temAudio && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      {incluirGraficos && dados.dailyData.length > 0 && (
        <div className="graf-card">
          <div className="graf-item">
            <p className="graf-titulo">Volume diário de inserções</p>
            <div className="graf-wrap">
              <BarChart width={680} height={230} data={dados.dailyData} margin={{ top: 18, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E3EBEE" />
                <XAxis dataKey="formattedDate" tick={{ fontSize: 11, fill: '#6C7C84' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#6C7C84' }} allowDecimals={false} width={34} />
                <Bar dataKey="count" fill="#0E5870" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="count" position="top" style={{ fontSize: 10, fill: '#0E5870' }} />
                </Bar>
              </BarChart>
            </div>
          </div>
          <div className="graf-item">
            <p className="graf-titulo">Distribuição por horário</p>
            <div className="graf-wrap">
              <LineChart width={680} height={210} data={dados.hourlyData} margin={{ top: 18, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E3EBEE" />
                <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#6C7C84' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6C7C84' }} allowDecimals={false} width={34} />
                <Line type="monotone" dataKey="count" stroke="#1C8FAE" strokeWidth={2} dot={{ r: 3, fill: '#1C8FAE' }} />
              </LineChart>
            </div>
          </div>
        </div>
      )}

      {observacao && observacao.trim() && (
        <div className="obs-card">
          <p className="obs-label">Observação</p>
          <p className="obs-text">{observacao}</p>
        </div>
      )}
    </div>
  );
};

export default CheckingReport;
